import { useEffect, useRef, useState } from "react";
import { FaTimes, FaCalendarAlt, FaClock, FaMapMarkerAlt, FaCalendarTimes } from "react-icons/fa";

const STATUS_MAP = {
  upcoming:  { label: "Upcoming",  bg: "rgba(14,127,187,.1)", col: "#0e7fbb" },
  ongoing:   { label: "Ongoing",   bg: "rgba(238,76,5,.1)",   col: "#ee4c05" },
  completed: { label: "Completed", bg: "rgba(4,52,99,.1)",    col: "#043463" },
  postponed: { label: "Postponed", bg: "rgba(238,76,5,.1)",   col: "#ee4c05" },
  cancelled: { label: "Cancelled", bg: "rgba(4,52,99,.1)",    col: "#043463" },
};

const fmtDate = (d) => {
  if (!d) return null;
  try {
    return new Date(d).toLocaleDateString("en-KE", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
  } catch {
    return null;
  }
};

const fmtTime = (t) => {
  if (!t || !t.includes(":")) return null;
  try {
    const [h, m] = t.split(":");
    const d = new Date();
    d.setHours(+h, +m);
    return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
  } catch {
    return null;
  }
};

const MODAL_CSS = `
  .edm-overlay {
    position: fixed; inset: 0; z-index: 1000;
    background: rgba(4,52,99,.55);
    display: flex; align-items: center; justify-content: center;
    padding: 1.25rem;
    opacity: 0; transition: opacity .25s ease;
  }
  .edm-overlay.edm-open { opacity: 1; }

  .edm-dialog {
    background: #ffffff;
    width: 100%; max-width: 640px;
    max-height: 90vh;
    border-radius: 12px;
    overflow: hidden;
    display: flex; flex-direction: column;
    transform: scale(.94) translateY(12px);
    opacity: 0;
    transition: transform .28s cubic-bezier(.16,1,.3,1), opacity .28s ease;
    box-shadow: 0 30px 60px rgba(4,52,99,.25);
  }
  .edm-overlay.edm-open .edm-dialog {
    transform: scale(1) translateY(0);
    opacity: 1;
  }

  .edm-header {
    display: flex; align-items: flex-start; justify-content: space-between;
    gap: 1rem;
    padding: 1.25rem 1.5rem;
    border-bottom: 1px solid rgba(4,52,99,.08);
    flex-shrink: 0;
  }
  .edm-title {
    font-family: 'Bebas Neue', sans-serif;
    letter-spacing: .03em;
    font-size: clamp(1.25rem, 3vw, 1.6rem);
    color: #043463;
    line-height: 1.1;
    margin: 0;
  }
  .edm-close {
    flex-shrink: 0;
    width: 36px; height: 36px;
    display: flex; align-items: center; justify-content: center;
    border-radius: 50%;
    border: none;
    background: rgba(4,52,99,.06);
    color: #043463;
    cursor: pointer;
    font-size: 1rem;
    transition: background .2s, color .2s, transform .2s;
  }
  .edm-close:hover { background: #ee4c05; color: #fff; transform: rotate(90deg); }

  .edm-body {
    overflow-y: auto;
    padding: 0 0 1.5rem;
  }

  .edm-poster {
  width: 100%;
  aspect-ratio: 16/9;
  background: linear-gradient(135deg,#f0f4f8,#e2e8f0);
  display: flex; align-items: center; justify-content: center;
  overflow: hidden;
}
.edm-poster img { width: 100%; height: 100%; object-fit: contain; display: block; }

  .edm-content { padding: 1.5rem; display: flex; flex-direction: column; gap: 1rem; }

  .edm-status-badge {
    display: inline-flex; align-items: center; gap: .35rem;
    font-size: .65rem; font-weight: 800; letter-spacing: .12em; text-transform: uppercase;
    padding: .35rem .8rem; border-radius: 100px;
    align-self: flex-start;
  }

  .edm-description {
    font-family: 'Outfit', sans-serif;
    font-size: .9rem; line-height: 1.75; color: #4a5568;
    margin: 0;
  }

  .edm-meta-grid {
    display: grid; grid-template-columns: 1fr; gap: .75rem;
    background: #f8fafc;
    border-radius: 8px;
    padding: 1rem 1.25rem;
  }
  @media (min-width: 480px) { .edm-meta-grid { grid-template-columns: 1fr 1fr; } }

  .edm-meta-item { display: flex; align-items: flex-start; gap: .625rem; }
  .edm-meta-icon { color: #ee4c05; font-size: .95rem; margin-top: .15rem; flex-shrink: 0; }
  .edm-meta-label {
    display: block; font-size: .62rem; font-weight: 800; letter-spacing: .1em;
    text-transform: uppercase; color: #94a3b8; margin-bottom: .15rem;
  }
  .edm-meta-value { font-size: .8rem; color: #043463; font-weight: 600; }

  .edm-state { padding: 3rem 1.5rem; text-align: center; color: #4a5568; }

  .edm-extra { display: flex; flex-direction: column; gap: 1rem; }

  @media (max-width: 480px) {
    .edm-overlay { padding: 0; align-items: flex-end; }
    .edm-dialog { max-width: 100%; max-height: 92vh; border-radius: 16px 16px 0 0; }
  }
`;

/**
 * Reusable event details modal.
 *
 * Takes the already-loaded `event` object directly (from the upcoming/past
 * list responses) rather than re-fetching by id — avoids depending on a
 * separate detail endpoint that may not exist on the backend.
 *
 * Extensibility: pass `renderExtraSections={(event) => <>...</>}` to inject
 * future content (ticket prices, sponsors, speakers, gallery grid, etc.)
 * right after the core meta grid, without modifying this component.
 *
 * Usage:
 *   <EventDetailsModal
 *     event={selectedEvent}
 *     isOpen={modalOpen}
 *     onClose={() => setModalOpen(false)}
 *   />
 */
export default function EventDetailsModal({ event, isOpen, onClose, renderExtraSections }) {
  const [visible, setVisible] = useState(false); // controls the enter animation class
  const overlayRef = useRef(null);

  // Mount/animate + body scroll lock
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = "0";
      document.body.style.right = "0";
      document.body.dataset.scrollY = String(scrollY);
      const raf = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(raf);
    } else {
      setVisible(false);
      const scrollY = parseInt(document.body.dataset.scrollY || "0", 10);
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
      window.scrollTo(0, scrollY);
    }
  }, [isOpen]);

  // Escape key
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  if (!isOpen || !event) return null;

  const cover = event.images?.[0];
  const posterSrc = cover ? (typeof cover === "string" ? cover : cover.image) : null;
  const status = STATUS_MAP[event.status] ?? STATUS_MAP.upcoming;
  const dateStr = fmtDate(event.event_date);
  const startStr = fmtTime(event.start_time);
  const endStr = fmtTime(event.end_time);
  const timeStr = startStr ? `${startStr}${endStr ? ` – ${endStr}` : ""}` : null;

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) onClose();
  };

  return (
    <div
      ref={overlayRef}
      className={`edm-overlay ${visible ? "edm-open" : ""}`}
      onMouseDown={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="edm-title"
    >
      <style>{MODAL_CSS}</style>
      <div className="edm-dialog">
        <div className="edm-header">
          <h2 id="edm-title" className="edm-title">{event.title ?? "Event Details"}</h2>
          <button type="button" className="edm-close" onClick={onClose} aria-label="Close event details">
            <FaTimes aria-hidden="true" />
          </button>
        </div>

        <div className="edm-body">
          <div className="edm-poster">
            {posterSrc ? (
              <img src={posterSrc} alt={`Event: ${event.title}`} />
            ) : (
              <FaCalendarTimes style={{ fontSize: "3rem", color: "rgba(4,52,99,.15)" }} aria-hidden="true" />
            )}
          </div>

          <div className="edm-content">
            <span className="edm-status-badge" style={{ background: status.bg, color: status.col }}>
              {status.label}
            </span>

            <p className="edm-description">
              {event.description && event.description.trim() ? event.description : "Not available"}
            </p>

            <div className="edm-meta-grid">
              <div className="edm-meta-item">
                <FaCalendarAlt className="edm-meta-icon" aria-hidden="true" />
                <div>
                  <span className="edm-meta-label">Date</span>
                  <span className="edm-meta-value">{dateStr ?? "Not available"}</span>
                </div>
              </div>
              <div className="edm-meta-item">
                <FaClock className="edm-meta-icon" aria-hidden="true" />
                <div>
                  <span className="edm-meta-label">Time</span>
                  <span className="edm-meta-value">{timeStr ?? "Not available"}</span>
                </div>
              </div>
              <div className="edm-meta-item" style={{ gridColumn: "1 / -1" }}>
                <FaMapMarkerAlt className="edm-meta-icon" aria-hidden="true" />
                <div>
                  <span className="edm-meta-label">Location</span>
                  <span className="edm-meta-value">{event.location ?? "Not available"}</span>
                </div>
              </div>
            </div>

            {/* Extension point for ticket prices, sponsors, speakers, gallery, etc. */}
            {typeof renderExtraSections === "function" && (
              <div className="edm-extra">{renderExtraSections(event)}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}